/**
 * @file FontCatalogList.tsx
 * @description Первый экран окна «Шрифт …» (phase19.16 Р1, Р1-7; SB10, SB99;
 * uiux-design-r1 §1 и проект своих шрифтов §1): поиск по названию, фильтр по
 * виду со счётчиками, свой текст образца; первым — блок «ВАШИ ШРИФТЫ»
 * (виден при любом фильтре вида, поиск отбирает и его), ниже — «ШРИФТЫ
 * ПЛАТФОРМЫ». Файл образца грузится только у видимых строк.
 *
 * До md экран компактный, чтобы список занимал остаток высоты (≥4 строк на
 * 360): фильтры — одна строка чипов с прокруткой вбок, поле «Текст образца»
 * свёрнуто в кнопку рядом с поиском и раскрывается по нажатию.
 */

import {
  listSelectableSiteFonts,
  SITE_FONT_CATEGORY_LABELS,
  type SiteFontCategory,
} from "@chaiberi/shared";
import { RotateCcw, Type } from "lucide-react";
import { useMemo, useState, type ReactNode, type RefObject } from "react";

import { Button } from "../../ui/button";
import { FilterChips } from "../../ui/FilterChips";
import { Input } from "../../ui/input";
import { SearchInput } from "../../ui/SearchInput";
import { SectionLabel } from "../../ui/SectionLabel";
import { useShopFonts } from "../shop-fonts-context";
import { FontCatalogRow } from "./FontCatalogRow";
import { ShopFontsBlock } from "./ShopFontsBlock";
import { pluralRu } from "../../../lib/plural";
import type { ShopFontDto } from "../../../lib/site-view/shop-fonts";
import { cn } from "@/lib/utils";

/** Текст образца по умолчанию (все буквы алфавита). */
export const FONT_SAMPLE_TEXT = "Съешь же ещё этих мягких французских булок, да выпей чаю";

export type CategoryFilter = SiteFontCategory | "all";

interface Props {
  initialCategory: CategoryFilter;
  chosen: string | null;
  current: string | null;
  /** Прокручиваемый список — окно возвращает в нём фокус и прокрутку. */
  listRef: RefObject<HTMLDivElement | null>;
  /** Быстрый выбор над «Вашими шрифтами» (окно шрифта элемента); при поиске скрыт. */
  leading?: ReactNode;
  onChoose: (key: string) => void;
  onUpload: () => void;
  onAddFile: (font: ShopFontDto) => void;
  onRename: (font: ShopFontDto) => void;
  onDelete: (font: ShopFontDto) => void;
}

const CAPTION = "Шрифты платформы — с кириллицей и свободной лицензией. За права на свои шрифты отвечает магазин.";

export function FontCatalogList({ initialCategory, chosen, current, listRef, leading, onChoose, onUpload, onAddFile, onRename, onDelete }: Props) {
  const shop = useShopFonts();
  const fonts = useMemo(() => listSelectableSiteFonts(), []);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryFilter>(initialCategory);
  const [sample, setSample] = useState(FONT_SAMPLE_TEXT);
  const [sampleOpen, setSampleOpen] = useState(false);

  const chips = useMemo(
    () => [
      { value: "all" as const, label: "Все", count: fonts.length },
      ...(Object.keys(SITE_FONT_CATEGORY_LABELS) as SiteFontCategory[]).map((c) => ({
        value: c,
        label: SITE_FONT_CATEGORY_LABELS[c],
        count: fonts.filter((f) => f.category === c).length,
      })),
    ],
    [fonts],
  );
  const needle = query.trim().toLowerCase();
  const shown = fonts.filter(
    (f) => (category === "all" || f.category === category) && (!needle || f.family.toLowerCase().includes(needle)),
  );
  const ownShown = shop.items.filter((f) => !needle || f.family.toLowerCase().includes(needle));
  const text = sample || FONT_SAMPLE_TEXT;

  return (
    <>
      <div className="flex shrink-0 items-center gap-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Найти шрифт по названию" ariaLabel="Найти шрифт" className="min-w-0 flex-1" />
        {sampleOpen ? null : (
          <Button variant="outline" size="icon" className="md:hidden" aria-label="Изменить текст образца" onClick={() => setSampleOpen(true)}>
            <Type className="size-5" />
          </Button>
        )}
      </div>
      <FilterChips
        aria-label="Вид шрифта"
        items={chips}
        value={category}
        onChange={setCategory}
        className="shrink-0 max-md:flex-nowrap max-md:overflow-x-auto max-md:pb-1 max-md:[&>*]:shrink-0"
      />
      <div className={cn("flex shrink-0 items-center gap-2", !sampleOpen && "max-md:hidden")}>
        <Input value={sample} onChange={(e) => setSample(e.target.value)} aria-label="Текст образца" className="flex-1" />
        <Button variant="ghost" size="icon" aria-label="Вернуть текст образца" onClick={() => setSample(FONT_SAMPLE_TEXT)}>
          <RotateCcw className="size-4" />
        </Button>
      </div>
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto rounded-xl [box-shadow:inset_0_0_0_1px_var(--border)]">
        {needle ? null : leading}
        <ShopFontsBlock
          shown={ownShown}
          sample={text}
          chosen={chosen}
          current={current}
          onUpload={onUpload}
          onSelect={onChoose}
          onAddFile={onAddFile}
          onRename={onRename}
          onDelete={onDelete}
        />
        <SectionLabel as="h2" className="border-t border-[var(--border)] px-4 pb-2 pt-3">
          Шрифты платформы
        </SectionLabel>
        {shown.length === 0 ? (
          <CatalogEmpty query={query} category={category} onAllCategories={() => setCategory("all")} onReset={() => setQuery("")} />
        ) : (
          <div role="radiogroup" aria-label="Шрифты платформы">
            {shown.map((font) => (
              <FontCatalogRow
                key={font.key}
                fontKey={font.key}
                family={font.family}
                meta={[
                  SITE_FONT_CATEGORY_LABELS[font.category],
                  `${font.weights.length} ${pluralRu(font.weights.length, ["начертание", "начертания", "начертаний"])}`,
                  font.key === current ? "сейчас" : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                sample={text}
                selected={font.key === chosen}
                onSelect={() => onChoose(font.key)}
              />
            ))}
          </div>
        )}
        {/* На телефоне подпись — в конце списка, чтобы не отнимать высоту у строк. */}
        <p className="type-caption p-4 text-on-surface-variant md:hidden">{CAPTION}</p>
      </div>
      <p className="type-caption shrink-0 text-on-surface-variant max-md:hidden">{CAPTION}</p>
    </>
  );
}

interface EmptyProps {
  query: string;
  category: CategoryFilter;
  onAllCategories: () => void;
  onReset: () => void;
}

/**
 * Ничего не нашлось. При фильтре вида — «В категории «Рукописные» не
 * найдено» и поиск во всех категориях с тем же запросом (QA 2026-10-11:
 * окно акцента открывается с «Рукописными», и «Roboto» выглядел как
 * отсутствующий в каталоге). Без фильтра — прежний совет про латиницу.
 */
function CatalogEmpty({ query, category, onAllCategories, onReset }: EmptyProps) {
  if (category !== "all") {
    return (
      <div className="flex flex-col items-start gap-2 p-4">
        <p className="type-body text-on-surface-variant">В категории «{SITE_FONT_CATEGORY_LABELS[category]}» не найдено.</p>
        {/* 48 на всех ширинах (QA): общая высота кнопок темы с 1024 — 44, правило вне слоёв — поэтому «!». */}
        <Button variant="outline" className="min-h-12!" onClick={onAllCategories}>
          Искать во всех категориях
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-2 p-4">
      <p className="type-body text-on-surface-variant">
        Шрифт «{query}» не найден. В каталоге только шрифты с кириллицей и свободной лицензией — попробуйте название латиницей.
      </p>
      <Button variant="outline" onClick={onReset}>
        Сбросить поиск
      </Button>
    </div>
  );
}
